import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import * as moment from 'moment';
import {
  calculatePromoPercentage,
  isEnrolledOrFree,
  promotionEndingMessage,
} from '../../../services/utilityFunctions';
import { ENROLL_SOURCE } from '../../../services/googleAnalytics';
import LoggedUserContext from '../../../contextProviders/LoggedUserContext';
import MetaContext from '../../../contextProviders/MetaContext';
import Anchor from '../../../commonComponents/Anchor';
import UserProp from '../../../commonComponents/propTypes/userProp';
import CourseProp from '../propTypes/courseProp';
import Progress from './enrolledItem/Progress';
import EnrollButton from '../EnrollButton';
import CourseContinueButton from '../details/CourseContinueButton';
import CountryProp from '../../../commonComponents/forms/Address/CountryProp';
import i18n from '../../../services/i18n';
import PriceDisplay from '../../../commonComponents/PriceDisplay';
import AcademyFooterContainer from '../../../commonComponents/container/AcademyFooterContainer';
import LanguagesIcon from '../../../commonComponents/icons/LanguagesIcon';
import CertificateIcon from '../../../commonComponents/icons/CertificateIcon';

const detailsLinkText = (loggedUser, course) => {
  if (isEnrolledOrFree(loggedUser, course) && course.progress === 0) {
    return 'start';
  }
  if (course.progress > 0 && course.progress < 100) {
    return 'continue';
  }
  return 'academy_details';
};

export default function AcademyCourse({
  t,
  languages,
  course,
  loggedUser: loggedUserParam,
  meta: metaParam,
  onEnroll,
  showProgress,
  showDetails,
  showEnroll,
  showCertificate,
  showCertificateInfo,
  showModules,
  className,
  showHardSkills,
  onCourseContinue,
  onCoursePlay,
  sessionId,
  showPromotion,
}) {
  const loggedUser =
    loggedUserParam || useContext(LoggedUserContext).loggedUser;
  const meta = metaParam || useContext(MetaContext).meta;

  const duration = moment.duration(course.duration);
  const hours =
    duration.days() > 0
      ? duration.days() * 24 + duration.hours()
      : duration.hours();
  const durationCompose = `${hours}h:${duration.minutes()}m`;

  const languageValue = useMemo(() => {
    let language = null;
    if (languages && languages.length > 0) {
      const lang = languages.find(l => l.code === course.language) || {};
      language = lang.native || t('all');
    }
    return language;
  }, [course.language, languages]);

  const shouldShowCertificate =
    showCertificate &&
    course.certificate &&
    course.certificate.isActive &&
    course.progress === 100;

  const userPrice =
    course.userPromotionPrice && course.userPromotionPrice.price
      ? course.userPromotionPrice
      : course.userPrice || null;
  const shouldShowEnroll = loggedUser
    ? !showDetails && showEnroll && userPrice
    : !showDetails;

  const shouldShowProgress =
    showProgress && isEnrolledOrFree(loggedUser, course);

  const shouldShowContinue = !shouldShowEnroll && onCourseContinue;

  const showActions =
    shouldShowCertificate ||
    shouldShowEnroll ||
    showDetails ||
    shouldShowContinue;

  const image = course.imageSquare || course.image;

  const containerClass = cx(
    'm-portlet',
    'm-portlet--rounded',
    'm-portlet--rounded-force',
    'academy-course-item',
    className,
  );

  const imageURL = image ? image.thumb : '/images/no_image_upload.png';
  const url = sessionId
    ? `/academy/${course.slug}?sessionContext=${sessionId}`
    : `/academy/${course.slug}`;

  const promotionMessage = promotionEndingMessage(course.promotionPriceDateEnd);
  const promoPercentage = calculatePromoPercentage(
    course?.userPrice?.total,
    course?.userPromotionPrice?.total,
  );
  const promoPercentageMessage =
    promoPercentage &&
    i18n.__('product.promotion.percentageOff', promoPercentage);

  const hardSkills = useMemo(
    () =>
      course.hardSkills && course.hardSkills.length > 0
        ? course.hardSkills.map(hs => hs.name).join(', ')
        : '',
    [course?.hardSkills],
  );
  return (
    <div>
      {showPromotion && promotionMessage?.length > 0 && (
        <div className="flag-academy flag-academy-promotion">{`${promoPercentageMessage} ${promotionMessage}`}</div>
      )}
      <div
        className={containerClass}
        itemScope
        itemType="http://schema.org/Course"
      >
        <div
          itemProp="courseCode"
          content={course._id}
          className="m-portlet__body course-container-body academy-course-item-container"
        >
          <div className="row">
            <div className="col-lg-2 col-12">
              <Anchor
                itemProp="url"
                content={`/academy/${course.slug}`}
                href={`/academy/${course.slug}/play`}
                className="course-image course-play-button"
                onClick={
                  onCoursePlay
                    ? evt => {
                        evt.preventDefault();
                        onCoursePlay();
                      }
                    : undefined
                }
              >
                <img
                  itemProp="image"
                  content={imageURL}
                  src={imageURL}
                  alt="course"
                />
              </Anchor>
            </div>
            <div className="col-lg-10 col-12">
              <div className="row">
                {/** NAME & PROGRESS & SUMMARY COURSE */}
                <div className="col-lg-9 col-12 academy-item-content">
                  <div className="course-details">
                    {/** NAME COURSE */}
                    <div className="course-header">
                      <span
                        itemProp="name"
                        content={course.name}
                        className="course-header-name"
                      >
                        {course.name}
                      </span>
                    </div>
                    {/** PROGRESS & SUMMARY COURSE */}
                    <div className="course-description-and-progress academy-progress-wrapper">
                      {shouldShowProgress && (
                        <Progress
                          progress={course.progress}
                          showLabel={false}
                        />
                      )}
                      {(course.summary || course.description) && (
                        <div
                          itemProp="description"
                          content={course.summary || course.description}
                          className="course-description"
                        >
                          {course.summary}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {/** PRICE */}
                <div
                  className="col-lg-3 col-12 px-0"
                  style={{ display: 'flex', alignItems: 'center' }}
                >
                  <PriceDisplay
                    type="course"
                    t={t}
                    userPrice={course?.userPrice}
                    userPromotionPrice={course?.userPromotionPrice}
                    withWrapper
                  />
                </div>
                {/** SKILLS */}
                {showHardSkills &&
                  (hardSkills.length > 0 || course?.skillLevel?.icon) && (
                    <div className="col-lg-12 col-12 academy-item-content">
                      <div className="skill-container-course">
                        <div className="label">{`${t(
                          'academy.skill.levels',
                        )}:`}</div>
                        {course?.skillLevel?.icon && (
                          <div className="skill-icon-container">
                            <img
                              src={course?.skillLevel?.icon}
                              alt="skill"
                              className="skill-icon"
                            />
                          </div>
                        )}
                        {hardSkills.length > 0 && (
                          <div className="skills">{hardSkills}</div>
                        )}
                      </div>
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>
        <div className="m-portlet__foot course-container-footer row">
          <div className="footer-info-container col-lg-8 col-12">
            {showModules && (
              <AcademyFooterContainer
                faIcon="la la-list"
                text={t('course.number.of.modules', course.modules)}
              />
            )}
            {duration > 0 && (
              <AcademyFooterContainer
                faIcon="la la-clock-o"
                text={durationCompose}
              />
            )}
            {languageValue && languageValue?.length > 0 && (
              <AcademyFooterContainer
                customIcon={<LanguagesIcon />}
                text={languageValue || ''}
              />
            )}
            {showCertificateInfo &&
              course.certificate &&
              course.certificate.isActive && (
                <AcademyFooterContainer
                  customIcon={<CertificateIcon />}
                  text={t('reward.certificate')}
                />
              )}
          </div>
          <div
            itemProp="provider"
            content={`${meta.url}#organization`}
            className="col-lg-3 col-12 px-0 academy-course-btn"
            style={{ display: 'flex', justifyContent: 'flex-end' }}
          >
            {showActions && (
              <div className="course-actions" style={{ maxWidth: '165px' }}>
                {showDetails && (
                  <Anchor
                    itemProp="url"
                    content={url}
                    href={url}
                    className="btn m-btn--pill btn-dynamic navigation-button"
                  >
                    {t(detailsLinkText(loggedUser, course))}
                  </Anchor>
                )}
                {shouldShowCertificate && (
                  <Anchor
                    href={`/academy/${course.slug}`}
                    className="btn m-btn--pill btn-metal navigation-button"
                  >
                    {t('academyDetails_printCertificate')}
                  </Anchor>
                )}
                {shouldShowEnroll && (
                  <EnrollButton
                    t={t}
                    onClick={onEnroll}
                    course={course}
                    source={ENROLL_SOURCE.ACADEMY}
                  />
                )}
                {shouldShowContinue && (
                  <CourseContinueButton
                    onClick={onCourseContinue}
                    course={course}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

AcademyCourse.propTypes = {
  t: PropTypes.func.isRequired,
  loggedUser: PropTypes.shape(UserProp.propType),
  meta: PropTypes.shape({ url: PropTypes.string }),
  course: PropTypes.shape(CourseProp.propType).isRequired,
  onEnroll: PropTypes.func,
  showDetails: PropTypes.bool,
  showModules: PropTypes.bool,
  showCertificateInfo: PropTypes.bool,
  showEnroll: PropTypes.bool,
  showProgress: PropTypes.bool,
  showCertificate: PropTypes.bool,
  showHardSkills: PropTypes.bool,
  onCourseContinue: PropTypes.func,
  onCoursePlay: PropTypes.func,
  className: PropTypes.string,
  languages: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType)),
  sessionId: PropTypes.string,
  showPromotion: PropTypes.bool,
};

AcademyCourse.defaultProps = {
  className: '',
  onEnroll: null,
  showDetails: true,
  showModules: true,
  showCertificateInfo: true,
  showEnroll: true,
  showProgress: false,
  showCertificate: false,
  showHardSkills: true,
  onCourseContinue: null,
  onCoursePlay: null,
  loggedUser: null,
  meta: null,
  languages: null,
  sessionId: null,
  showPromotion: false,
};
